/**
 * IP probe configuration and types.
 * The probe endpoint is swappable without code changes — just update the URLs.
 *
 * Working endpoints (verified May 2026):
 *   Primary: whatismyip.technology/api/me — returns IP, country, city, ISP, ASN
 *   Fallback: geo.kamero.ai/api/geo — returns IP, country, city (Vercel edge)
 *
 * For Direct mode, the endpoint must send permissive CORS headers.
 * For Nym mode, CORS is not a concern (request goes through WASM bundle).
 */

export interface ProbeResult {
  ip: string;
  country: string;
  city: string;
  isp: string;
  asn: string;
  org: string;
  /** Timestamp when the probe was executed */
  timestamp: number;
}

export const PROBE_ENDPOINTS = {
  primary: "https://whatismyip.technology/api/me",
  fallback: "https://geo.kamero.ai/api/geo",
} as const;

/**
 * Transform raw API response into our standard ProbeResult format.
 * Supports multiple API providers:
 *   - whatismyip.technology/api/me
 *   - geo.kamero.ai/api/geo
 *   - ip-api.com/json
 */
function transformResponse(data: Record<string, unknown>, source: string): ProbeResult {
  // whatismyip.technology/api/me format
  if (data.ip && data.isp) {
    return {
      ip: String(data.ip),
      country: String(data.country ?? data.countryCode ?? ""),
      city: String(data.city ?? ""),
      isp: String(data.isp ?? data.org ?? ""),
      asn: String(data.as ?? data.asn ?? ""),
      org: String(data.org ?? data.isp ?? ""),
      timestamp: Date.now(),
    };
  }

  // geo.kamero.ai/api/geo format (Vercel edge, fewer fields — no ISP)
  if (data.ip && data.country && !data.isp && !data.organization) {
    return {
      ip: String(data.ip),
      country: String(data.country ?? ""),
      city: String(data.city ?? ""),
      isp: `Kamero AI (${data.region ?? "unknown"})`,
      asn: "",
      org: "",
      timestamp: Date.now(),
    };
  }

  // ip-api.com/json format
  if (data.query) {
    return {
      ip: String(data.query),
      country: String(data.country ?? ""),
      city: String(data.city ?? ""),
      isp: String(data.isp ?? data.org ?? ""),
      asn: String(data.as ?? ""),
      org: String(data.org ?? ""),
      timestamp: Date.now(),
    };
  }

  // Generic fallback — try to extract what we can
  if (data.ip) {
    return {
      ip: String(data.ip ?? ""),
      country: String(data.country ?? data.country_name ?? data.countryCode ?? ""),
      city: String(data.city ?? data.cityName ?? ""),
      isp: String(data.isp ?? data.org ?? data.organization ?? ""),
      asn: String(data.asn ?? data.as ?? data.asnNumber ?? ""),
      org: String(data.org ?? data.organization ?? ""),
      timestamp: Date.now(),
    };
  }

  throw new Error(`Unknown response format from ${source}: ${JSON.stringify(data).slice(0, 200)}`);
}

/**
 * Run a probe using standard fetch (Direct mode).
 */
export async function probeDirect(url: string): Promise<ProbeResult> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);

  try {
    const res = await fetch(url, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      throw new Error(`Probe returned ${res.status}: ${res.statusText}`);
    }
    const data = await res.json();
    if (data?.error === true || data?.status === "error") {
      throw new Error(`Probe error: ${data.message ?? data.reason ?? "unknown"}`);
    }
    return transformResponse(data as Record<string, unknown>, url);
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Try primary endpoint first, fall back through the list on failure.
 */
export async function probeWithFallback(): Promise<ProbeResult> {
  const errors: string[] = [];
  for (const [name, url] of Object.entries(PROBE_ENDPOINTS)) {
    try {
      return await probeDirect(url);
    } catch (e) {
      errors.push(`${name}: ${(e as Error).message}`);
    }
  }
  throw new Error(`All probe endpoints failed: ${errors.join("; ")}`);
}

/**
 * Run a probe through a custom fetch function (e.g. mixFetch for Nym mode).
 */
export async function probeWithCustomFetch(
  customFetch: typeof fetch,
  url: string = PROBE_ENDPOINTS.primary,
): Promise<ProbeResult> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);

  try {
    const res = await customFetch(url, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      throw new Error(`Probe returned ${res.status}: ${res.statusText}`);
    }
    const data = await res.json();
    if (data?.error === true || data?.status === "error") {
      throw new Error(`Probe error: ${data.message ?? data.reason ?? "unknown"}`);
    }
    return transformResponse(data as Record<string, unknown>, url);
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Fetch user agent for display.
 */
export function getUserAgent(): string {
  return navigator.userAgent;
}
